import { LEADER_PERM_TABS } from "lib/leaderPerms";

export const ROLES = Object.freeze({
  ADMIN: "Admin",
  ACCOUNT_MANAGER: "Account Manager",
  CLIENT: "Client",
  TEAM_MEMBER: "Team Member",
  SUPER_CLIENT: "Super Client",
  FINANCIAL_MANAGER: "Financial Manager",
  MEDIA_BUYER: "Media Buyer",
  MEDIA_BUYING_HEAD: "Media Buying Head",
  MEDIA_BUYING_TEAM_LEADER: "Media Buying Team Leader",
});

export const isAdmin = (user) => user?.role === ROLES.ADMIN;
export const isAccountManager = (user) => user?.role === ROLES.ACCOUNT_MANAGER;
export const isFinancialManager = (user) =>
  user?.role === ROLES.FINANCIAL_MANAGER;
export const isMediaBuyer = (user) => user?.role === ROLES.MEDIA_BUYER;
export const isMediaBuyingHead = (user) =>
  user?.role === ROLES.MEDIA_BUYING_HEAD;
// Runs one squad of the buying team. Mirrors lib/roles.js; the data clamp to
// their squad is the server's (lib/mediaBuying/scope.js), never the UI's.
export const isMediaBuyingTeamLeader = (user) =>
  user?.role === ROLES.MEDIA_BUYING_TEAM_LEADER;

// True for users that should see the Financial Manager dashboard:
//   - the dedicated Financial Manager role, OR
//   - any Admin (all admins get access).
export const canViewFinancialDashboard = (user) =>
  isFinancialManager(user) || isAdmin(user);

// Who can open the Media Buying module: Admin (full CRM), the Media Buyer
// (scoped to their own assignments downstream), and the Media Buying Head
// (oversight of all buyers). Mirrors lib/roles.js on the backend.
export const canViewMediaBuying = (user) =>
  isAdmin(user) ||
  isMediaBuyer(user) ||
  isMediaBuyingHead(user) ||
  isMediaBuyingTeamLeader(user);

// Who can open the Budget Tracker. Admin + Financial Manager (whole Financial
// dashboard) plus the Media Buying Head, whose ONLY financial-related surface
// is the Budget Tracker.
export const canViewBudgetTracker = (user) =>
  isAdmin(user) || isFinancialManager(user) || isMediaBuyingHead(user);

// Media Buying roles are sealed to their module (mirrors how a Financial
// Manager is sealed to Financial) — used to drive a dedicated, minimal nav.
export const isMediaBuyingOnly = (user) =>
  isMediaBuyer(user) || isMediaBuyingHead(user) || isMediaBuyingTeamLeader(user);

// Media Buying "manager" — full module powers: the Manage/History/Bonus tabs,
// bulk holds, VIP toggles, acting on any client. Admin or Media Buying Head.
// Accepts both the role string and getMe()'s isAdmin boolean flag (the MB
// panels read `me` from getMe, which carries the flag). Replaces the
// `me?.isAdmin || me?.role === "Media Buying Head"` checks that were inlined
// (and drifting) across ~6 panel files.
export const isMbManager = (user) =>
  !!user?.isAdmin || isAdmin(user) || isMediaBuyingHead(user);

// Media Buying "team manager" — isMbManager PLUS a Team Leader. The panels use
// this to decide whether to render the controls that act on a buyer or a
// client at all; the server then decides whether THIS target is in reach
// (requireMbSquadManage + assertOwnsClient). A leader with no controls could
// not run her team, and a leader with controls on another team's client gets a
// plain 403 saying whose it is — which is the right failure, and the only one
// the UI cannot prevent without duplicating the org chart.
//
// Anything GLOBAL — the shared buyer roster, client merges, the CPL config,
// aliases, broadcasts, the automation sweeps — must keep using isMbManager.
export const isMbTeamManager = (user) =>
  isMbManager(user) || isMediaBuyingTeamLeader(user);

// Which Media Buying tabs this Team Leader may open. The server ships the
// complete answer on getMe() as `mbLeaderPerms` (every tab, gaps already
// filled), so this is a lookup and NOT a place to re-implement the defaulting
// rule — see lib/mediaBuying/leaderPerms.js, which owns it.
export const canLeaderViewTab = (user, key) => {
  if (!isMediaBuyingTeamLeader(user)) return true;
  // A locked tab is locked whatever the payload says. The server already
  // refuses to store `overview: false` and /me forces it true, so this cannot
  // normally be reached — but a nav that renders nothing is an app that looks
  // broken, and not depending on someone else's sanitising is cheaper than
  // the bug report.
  if (LEADER_PERM_TABS.find((t) => t.key === key)?.locked) return true;
  const perms = user?.mbLeaderPerms;
  // No map yet (an older token, or /me not back yet): show the tab. The route
  // and the API are the real locks, so the cost of guessing wrong here is a
  // tab that 403s, not data on screen.
  if (!perms) return true;
  return perms[key] !== false;
};

// Feedback bridge — who can flag a campaign to its media buyer. The action
// lives on a client's page, which is the account managers' surface: they hold
// the client feedback and decide what deserves a buyer's attention. The Media
// Buying Head keeps it too (oversight of the whole loop).
//
// The GALLERY half needs no helper — it's a Media Buying section, already
// gated by the nav (MB_SECTION_ITEMS) and the module's own route guard.
// Mirrored by requireFeedbackNotify in middleware/requireFeedbackAccess.js,
// which is the actual lock; this only decides whether the panel renders.
export const canUseFeedbackNotify = (user) =>
  !!user?.isAdmin || isAdmin(user) || isAccountManager(user) || isMediaBuyingHead(user);

// The Media Buying Head reaches the CRM lead desk to see the leads their
// campaigns produced and to put an unassigned one on the right client — and
// nothing else. Mirrors the API allow-list in
// middleware/mediaBuyingHeadCrmAccess.js, which is the actual boundary; this
// only decides whether a control is worth showing. A button that is certain to
// 403 is worse than no button.
export const isLeadDeskViewer = (user) => isMediaBuyingHead(user);

// "Admin-like" — users that access the admin-style CRM surfaces.
export const canViewAdminSurfaces = (user) =>
  isAdmin(user) || isAccountManager(user);

// True when the user can perform mutating actions on Admin-style pages.
// Account Managers are view-only, so this is equivalent to isAdmin for now.
export const canEditOnAdminSurfaces = (user) => isAdmin(user);

// Managed-client lead grids let AMs update only follow-up fields while the
// backend keeps assignment and lead identity fields admin-only.
export const canEditManagedLeadFeedback = (user) =>
  isAdmin(user) || isAccountManager(user);

export const getPersonalClientId = (user) => user?.personalClientId ?? null;

export const isPersonalLead = (user, lead) => {
  const personal = getPersonalClientId(user);
  if (!personal) return false;
  return String(lead?.clientId ?? "") === String(personal);
};

export const isWrongNumberLead = (lead) => lead?.feedback === "Wrong Number";

// True if the given user can edit feedback/notes/status on this lead.
// Admins: yes on everything. AMs: only on their personal-book leads.
export const canEditPersonalLead = (user, lead) => {
  if (isAdmin(user)) return true;
  if (!isAccountManager(user)) return false;
  return isPersonalLead(user, lead);
};

// True if phones should be visible for this user on this lead. Every role
// sees them, Account Managers included (Mostafa, 2026-10-06 — the AM phone
// redaction was reversed; the backend helper redactLeadsForRole no longer
// strips anything either). Kept as a function so the grids and cards have one
// switch to flip if the rule ever comes back.
export const canSeeLeadPhones = (_user, _lead) => true;

// ── Teams (the buyer competition) ──
// Three named people, not a role — mirrors TEAMS_COMPETITION_VIEWERS in
// lib/roles.js on the backend, which is the actual boundary (the /teams routes
// 403 everyone else). This only decides whether the sidebar item is worth
// showing; a tab that is certain to 403 is worse than no tab.
export const TEAMS_COMPETITION_VIEWERS = Object.freeze([
  "64e52274a29b8e0b86357e32", // Mostafa
  "65f05898cb39fa2e8fa44cf7", // Mohamed Barakat
  "6a58db28a55513cd9b92900e", // Youssef Khaled
]);

export const canViewTeams = (user) => {
  if (!user) return false;
  const id = String(user._id ?? user.id ?? "");
  return (isAdmin(user) || isMediaBuyingHead(user)) && TEAMS_COMPETITION_VIEWERS.includes(id);
};

// Weekend Doubles: the same three people (the cover itself applies to every
// buyer; this is only the page).
export const canViewWeekendDoubles = canViewTeams;

// The Teams tab — the org chart (both teams, their leaders, their members).
// NOT canViewTeams, which is the three-id allow-list for the competition and
// the Weekend Doubles pairs. Mirrors canViewSquads in lib/roles.js.
export const canViewSquads = (user) =>
  !!user?.isAdmin || isAdmin(user) || isMediaBuyingHead(user);

// ── Personal workspace (the floating to-do / notes / reminders drawer) ──
// The Media Buying Head, plus named people by id — mirrors
// PERSONAL_WORKSPACE_USERS in lib/roles.js, which is the actual lock.
export const PERSONAL_WORKSPACE_USERS = Object.freeze([
  "69dc1936d6ec084f05169a9f", // Lamya Amr
]);

export const canUsePersonalWorkspace = (user) => {
  if (!user) return false;
  if (isMediaBuyingHead(user)) return true;
  const id = String(user._id ?? user.id ?? "");
  return PERSONAL_WORKSPACE_USERS.includes(id);
};
